import React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

// Import pages
import Blog from './pages/Blog';
import DynamicArticle from './pages/DynamicArticle';
import NotFound from './pages/NotFound';
import OnePagerHome from './pages/OnePagerHome';
import PracticeAreaPage from './pages/PracticeAreaPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PropertyLaw from './pages/PropertyLaw';
import CookieConsent from './components/CookieConsent';
import { useLanguage } from './contexts/LanguageContext';
import type { PublicPageData } from './lib/publicPage';

interface PublicAppProps {
  adminElement?: React.ReactNode;
  initialData?: PublicPageData | null;
}

const RestoreLanguagePreference = () => {
  const { pathname } = useLocation();
  const { setLanguage } = useLanguage();
  const restored = React.useRef(false);

  React.useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (!['/', '/blog', '/privacy-policy'].includes(pathname)) return;
    try {
      const saved = window.localStorage.getItem('app-language');
      if (saved === 'bg' || saved === 'en') setLanguage(saved);
    } catch {
      return;
    }
  }, [pathname, setLanguage]);

  return null;
};

const PublicApp: React.FC<PublicAppProps> = ({ adminElement, initialData }) => (
  <div className="min-h-screen bg-white">
    <Routes>
      <Route path="/" element={<OnePagerHome />} />
      <Route path="/blog" element={<Blog initialData={initialData} />} />
      <Route path="/blog/:slug" element={<DynamicArticle initialData={initialData} />} />
      <Route path="/semeino-pravo-roditelski-prava" element={<PracticeAreaPage area="family" />} />
      <Route path="/family-law-parental-rights" element={<PracticeAreaPage area="family" />} />
      <Route path="/zashtita-dlazhnitsi-potrebiteli" element={<PracticeAreaPage area="debtor" />} />
      <Route path="/debtor-consumer-protection" element={<PracticeAreaPage area="debtor" />} />
      <Route path="/veshtno-pravo-nedvizhimi-imoti" element={<PropertyLaw />} />
      <Route path="/property-law-real-estate" element={<PropertyLaw />} />
      <Route path="/administrativno-pravo" element={<PracticeAreaPage area="administrative" />} />
      <Route path="/administrative-law" element={<PracticeAreaPage area="administrative" />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      {adminElement && (
        <>
          {/* Admin Routes */}
          <Route path="/secure-panel" element={adminElement} />
        </>
      )}
      <Route path="*" element={<NotFound />} />
    </Routes>
    <CookieConsent />
    <RestoreLanguagePreference />
  </div>
);

export default PublicApp;
